import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../auth/DriverAuth";

export default function DriverApplicationForm({ onSuccess }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    id_number: "",
    car_plate_number: "",
    car_model: "",
    phone_number: "",
    physical_address: "",
    gender: "",
  });
  const [files, setFiles] = useState({
    license_image: null,
    id_image: null,
    selfie_image: null,
    car_images: [],
  });
  const [previews, setPreviews] = useState({
    license_image: null,
    id_image: null,
    selfie_image: null,
    car_images: [],
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviews((prev) => ({ ...prev, [type]: reader.result }));
      };
      reader.readAsDataURL(file);
      setFiles((prev) => ({ ...prev, [type]: file }));
    }
  };

  const handleCarImagesChange = (e) => {
    const selectedFiles = Array.from(e.target.files).slice(0, 4);
    const remainingSlots = 4 - files.car_images.length;
    const filesToAdd = selectedFiles.slice(0, remainingSlots);
    if (filesToAdd.length > 0) {
      const newPreviews = [];
      filesToAdd.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          newPreviews.push(reader.result);
          if (newPreviews.length === filesToAdd.length) {
            setPreviews((prev) => ({ ...prev, car_images: [...prev.car_images, ...newPreviews] }));
          }
        };
        reader.readAsDataURL(file);
      });
      setFiles((prev) => ({ ...prev, car_images: [...prev.car_images, ...filesToAdd] }));
    }
  };

  const handleRemoveImage = (index) => {
    setFiles((prev) => ({ ...prev, car_images: prev.car_images.filter((_, i) => i !== index) }));
    setPreviews((prev) => ({ ...prev, car_images: prev.car_images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.id_number.length !== 13 || !/^[0-9]+$/.test(formData.id_number)) {
      alert("ID number must be exactly 13 digits");
      return;
    }
    if (formData.phone_number.length !== 10 || !/^[0-9]+$/.test(formData.phone_number)) {
      alert("Phone number must be exactly 10 digits");
      return;
    }
    if (!files.license_image) {
      alert("Please upload your license image");
      return;
    }
    if (!files.id_image) {
      alert("Please upload your ID document image");
      return;
    }
    if (files.car_images.length === 0) {
      alert("Please upload at least one car image");
      return;
    }

    setLoading(true);

    const formDataToSend = new FormData();
    formDataToSend.append("license_image", files.license_image);
    formDataToSend.append("id_image", files.id_image);
    if (files.selfie_image) formDataToSend.append("selfie_image", files.selfie_image);
    files.car_images.forEach((file) => {
      formDataToSend.append("car_images", file);
    });

    try {
      const queryParams = new URLSearchParams({
        id_number: formData.id_number,
        car_plate_number: formData.car_plate_number,
        car_model: formData.car_model,
        phone_number: formData.phone_number,
        physical_address: formData.physical_address,
        gender: formData.gender,
      }).toString();

      const token = getAccessToken();
      if (!token) {
        alert("You are not logged in. Please log in and try again.");
        setLoading(false);
        return;
      }

      const response = await fetch(
        `https://werideapp-backend-production.up.railway.app/driver/driver-profile/apply?${queryParams}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formDataToSend,
        }
      );

      const responseText = await response.text();

      if (response.ok) {
        alert("Application submitted successfully! Our team will review your application.");
        if (onSuccess) onSuccess();
      } else {
        alert(`Submission failed: ${responseText}`);
      }
    } catch (error) {
      console.error("Application failed:", error);
      alert(`Network error: ${error.message}. Please check your connection and try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Driver Application</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">ID Number</label>
                  <input type="text" name="id_number" required maxLength={13} value={formData.id_number}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Car Plate Number</label>
                  <input type="text" name="car_plate_number" required value={formData.car_plate_number}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Car Model</label>
                  <input type="text" name="car_model" required value={formData.car_model}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input type="text" name="phone_number" required maxLength={10} value={formData.phone_number}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Physical Address</label>
                  <input type="text" name="physical_address" required value={formData.physical_address}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Gender</label>
                <select name="gender" required value={formData.gender} onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500">
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Selfie */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Selfie Photo</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    {previews.selfie_image ? (
                      <img src={previews.selfie_image} alt="Selfie" className="mx-auto h-32 w-auto rounded-full" />
                    ) : (
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    <label className="cursor-pointer font-medium text-emerald-600 hover:text-emerald-500">
                      <span>Take selfie</span>
                      <input type="file" className="sr-only" accept="image/*" capture="user"
                        onChange={(e) => handleFileChange(e, "selfie_image")} required />
                    </label>
                    <p className="text-xs text-gray-500">A clear photo of your face</p>
                  </div>
                </div>
              </div>

              {/* License */}
              <div>
                <label className="block text-sm font-medium text-gray-700">License Image</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    {previews.license_image ? (
                      <img src={previews.license_image} alt="License Preview" className="mx-auto h-32 w-auto" />
                    ) : (
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    <label className="cursor-pointer font-medium text-emerald-600 hover:text-emerald-500">
                      <span>Upload a file</span>
                      <input type="file" className="sr-only" onChange={(e) => handleFileChange(e, "license_image")} accept="image/*" capture="environment" required />
                    </label>
                  </div>
                </div>
              </div>

              {/* ID Document */}
              <div>
                <label className="block text-sm font-medium text-gray-700">ID Document Image</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    {previews.id_image ? (
                      <img src={previews.id_image} alt="ID Preview" className="mx-auto h-32 w-auto" />
                    ) : (
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    <label className="cursor-pointer font-medium text-emerald-600 hover:text-emerald-500">
                      <span>Upload a file</span>
                      <input type="file" className="sr-only" onChange={(e) => handleFileChange(e, "id_image")} accept="image/*" capture="environment" required />
                    </label>
                  </div>
                </div>
              </div>

              {/* Car Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Car Images (1-4 images)</label>
                <div className="mt-1 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {previews.car_images.map((preview, index) => (
                    <div key={index} className="relative">
                      <img src={preview} alt={`Car ${index + 1}`} className="h-24 w-full object-cover rounded-lg" />
                      <button type="button" onClick={() => handleRemoveImage(index)}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {previews.car_images.length < 4 && (
                    <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <label className="cursor-pointer font-medium text-emerald-600 hover:text-emerald-500">
                          <span>Upload files</span>
                          <input type="file" multiple className="sr-only" onChange={handleCarImagesChange}
                            accept="image/*" required={files.car_images.length === 0} />
                        </label>
                        <p className="text-xs text-gray-500">{4 - previews.car_images.length} remaining</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-5">
                <button type="submit" disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:bg-gray-400">
                  {loading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </div>
                  ) : "Submit Application"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
